import { Dispatch } from 'redux'

type ThunkInner<R> = (dispatch: Dispatch) => Promise<R>

const defaultKey = (...args: unknown[]) => JSON.stringify(args)

export function coalesceThunk<R>(
  factory: () => ThunkInner<R>,
): () => ThunkInner<R> {
  let inFlight: Promise<R> | null = null
  return () => async (dispatch: Dispatch) => {
    if (inFlight) return inFlight
    inFlight = (async () => {
      try {
        return await factory()(dispatch)
      } finally {
        inFlight = null
      }
    })()
    return inFlight
  }
}

export function coalesceThunkByKey<A extends unknown[], R>(
  factory: (...args: A) => ThunkInner<R>,
  keyFn: (...args: A) => string = defaultKey as (...args: A) => string,
): (...args: A) => ThunkInner<R> {
  const inFlight = new Map<string, Promise<R>>()
  return (...args: A) =>
    async (dispatch: Dispatch) => {
      const key = keyFn(...args)
      const existing = inFlight.get(key)
      if (existing) return existing
      const promise = (async () => {
        try {
          return await factory(...args)(dispatch)
        } finally {
          inFlight.delete(key)
        }
      })()
      inFlight.set(key, promise)
      return promise
    }
}

export function coalescePromise<R>(
  factory: () => Promise<R>,
): () => Promise<R> {
  let inFlight: Promise<R> | null = null
  return () => {
    if (inFlight) return inFlight
    inFlight = factory().finally(() => {
      inFlight = null
    })
    return inFlight
  }
}

export function coalescePromiseByKey<A extends unknown[], R>(
  factory: (...args: A) => Promise<R>,
  keyFn: (...args: A) => string = defaultKey as (...args: A) => string,
): (...args: A) => Promise<R> {
  const inFlight = new Map<string, Promise<R>>()
  return (...args: A) => {
    const key = keyFn(...args)
    const existing = inFlight.get(key)
    if (existing) return existing
    const promise = factory(...args).finally(() => {
      inFlight.delete(key)
    })
    inFlight.set(key, promise)
    return promise
  }
}
